import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNotification } from '../../../store/redux/hooks'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { useSendAdministrativeNotificationMutation } from '../../../store/redux/reducers/pushNotificationSlice'
import { Project, User } from '../../../store/types'
import { StyledContainerColumnLeft, StyledContainerRowStart } from '../../../styles/ContainerStyles'
import { getGap } from '../../../styles/stylePropsFunctions'
import SettingTile from '../../../views/GeneralSettingsView/SettingTile'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import DiscardChangesDialog from '../../Dialog/DiscardChangesDialog'
import RecipientsTabs from './RecipientsTabs'

const AdministrativeNotificationDialog = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { displayNotification } = useNotification()

  const [open, setOpen] = useState(false)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const [isSendToAll, setIsSendToAll] = useState(false)
  const [message, setMessage] = useState('')
  const [selectedEmployees, setSelectedEmployees] = useState<User[]>([])
  const [selectedLeaders, setSelectedLeaders] = useState<User[]>([])
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([])
  const currentUser = useSelector(getCurrentUser)
  const [sendNotification] = useSendAdministrativeNotificationMutation()
  const [tabValue, setTabValue] = React.useState(0)

  const checkUnsavedChanges = () =>
    isSendToAll ||
    message.length > 0 ||
    selectedEmployees.length > 0 ||
    selectedLeaders.length > 0 ||
    selectedProjects.length > 0

  const resetForm = () => {
    setMessage('')
    setSelectedEmployees([])
    setSelectedLeaders([])
    setSelectedProjects([])
    setIsSendToAll(false)
    setTabValue(0)
  }

  const isSendButtonDisabled =
    (selectedEmployees.length === 0 && selectedLeaders.length === 0 && selectedProjects.length === 0 && !isSendToAll) ||
    message.length === 0

  const handleConfirm = async () => {
    setIsConfirmationDialogOpen(false)
    setOpen(false)

    try {
      await sendNotification({
        employeeIds: tabValue === 0 && !isSendToAll ? selectedEmployees.map(u => u.id) : [],
        leaderIds: tabValue === 1 && !isSendToAll ? selectedLeaders.map(l => l.id) : [],
        projectIds: tabValue === 2 && !isSendToAll ? selectedProjects.map(l => l.id) : [],
        message,
        sendToAllEmployees: isSendToAll,
      }).unwrap()

      resetForm()

      displayNotification({
        open: false,
        type: 'success',
        message: t('components.notificationSidebar.administrativeNotification.successMessage'),
        timeout: 5000,
      })
    } catch (error) {
      displayNotification({
        open: false,
        type: 'error',
        message: t('components.notificationSidebar.administrativeNotification.errorMessage'),
        timeout: 5000,
      })
    }
  }

  const handleCancel = () => {
    if (checkUnsavedChanges()) {
      setIsCancelDialogOpen(true)
    } else {
      resetForm()
      setOpen(false)
    }
  }

  useEffect(() => {
    if (isSendToAll) {
      setTabValue(0)
      setSelectedEmployees([])
      setSelectedLeaders([])
      setSelectedProjects([])
    }
  }, [isSendToAll])

  return (
    <React.Fragment>
      <SettingTile
        icon={<NotificationsActiveIcon />}
        title={t('page.adminsPage.generalSettings.administrativeNotification.title')}
        description={t('page.adminsPage.generalSettings.administrativeNotification.description')}
        onSelect={() => setOpen(prev => !prev)}
      />

      <Dialog
        sx={{ color: 'text.base' }}
        PaperProps={{ elevation: 2, sx: { maxWidth: '600px', width: '100%' } }}
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && checkUnsavedChanges()) {
            setIsCancelDialogOpen(true)
          } else resetForm()
        }}
        aria-labelledby={'send-administrative-modification-dialog-title'}
      >
        <StyledContainerColumnLeft
          sx={{
            p: 4,
            gap: getGap(isSmallScreen),
          }}
        >
          <StyledContainerRowStart sx={{ width: '100%' }}>
            <StyledContainerColumnLeft sx={{ width: '100%' }}>
              <Typography
                component={'h3'}
                variant="h4"
                color="text.highEmphasis"
                id={'send-administrative-modification-dialog-title'}
              >
                {t('components.notificationSidebar.administrativeNotification.title')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.notificationSidebar.administrativeNotification.description')}
              </Typography>
            </StyledContainerColumnLeft>
          </StyledContainerRowStart>

          <FormControlLabel
            control={
              <Checkbox
                style={{ marginRight: 8 }}
                checked={isSendToAll}
                onChange={e => setIsSendToAll(e.target.checked)}
              />
            }
            label={t('components.notificationSidebar.missingHoursNotification.sendAll')}
            sx={{
              marginTop: -2,
              marginBottom: -2,
              color: isSendToAll ? 'primary.main' : '',
              '& .MuiFormControlLabel-label': {
                fontWeight: 500,
              },
              width: 'fit-content',
            }}
          />

          {!isSendToAll && (
            <RecipientsTabs
              tabValue={tabValue}
              setTabValue={setTabValue}
              selectedEmployees={selectedEmployees}
              setSelectedEmployees={setSelectedEmployees}
              selectedLeaders={selectedLeaders}
              setSelectedLeaders={setSelectedLeaders}
              selectedProjects={selectedProjects}
              setSelectedProjects={setSelectedProjects}
              currentUser={currentUser}
            />
          )}

          <TextField
            label={t('components.notificationSidebar.administrativeNotification.placeHolder')}
            placeholder={t('components.notificationSidebar.administrativeNotification.descriptionPlaceholder')}
            value={message}
            onChange={e => setMessage(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            multiline
            rows={8}
          />

          <Stack gap={2} justifyContent="flex-end" direction="row">
            <Button color="secondary" onClick={handleCancel} sx={{ fontWeight: 'bold' }}>
              {t('components.notificationSidebar.administrativeNotification.cancel')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="medium"
              disabled={isSendButtonDisabled}
              onClick={() => setIsConfirmationDialogOpen(true)}
            >
              {t('components.notificationSidebar.administrativeNotification.send')}
            </Button>
          </Stack>
        </StyledContainerColumnLeft>
      </Dialog>
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        setOpen={setIsConfirmationDialogOpen}
        handleConfirm={handleConfirm}
        title={t('components.notificationSidebar.dialog.sendNotificationTitle')}
        content={t('components.notificationSidebar.dialog.sendNotificationContent')}
      />
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        actionOnConfirm={() => {
          resetForm()
          setOpen(false)
        }}
      />
    </React.Fragment>
  )
}

export default AdministrativeNotificationDialog
